import React, { ComponentPropsWithoutRef } from "react";

import ChevronDownIcon from "shared/components/icons/chevron/ChevronDownIcon";
import ChevronUpIcon from "shared/components/icons/chevron/ChevronUpIcon";
import { PillarTableColumnProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { DeepKeys } from "shared/types/deepkeys";

export type PillarTableNestedToggleProps<T = any> =
  ComponentPropsWithoutRef<"div"> & {
    children: Array<React.ReactElement<PillarTableColumnProps<T>>>;
    valueProperty: DeepKeys<T>;
    pagination?: boolean;
    urlParams?: boolean;
    queryKeyProperty: DeepKeys<T>;
  };

/**
 * The expand/collapse arrow, rendered inside the row's first cell.
 *
 * It toggles the row itself and swallows the click, so it keeps working when
 * that first cell is a link — the arrow used to be dead space over a `linkTo`
 * column, which navigated away instead of expanding.
 */
const PillarTableNestedToggle = () => {
  const { rowExpanded, setRowExpanded, rowIndex } = usePillarTableRowContext();
  const { testidTable } = usePillarTableContext();

  if (rowExpanded == "none") {
    return <></>;
  }
  const expanded = rowExpanded === "open";
  return (
    <button
      type="button"
      // A cell click can navigate; expanding the row is a different intent.
      onClick={(event) => {
        event.stopPropagation();
        setRowExpanded(expanded ? "close" : "open");
      }}
      aria-expanded={expanded}
      aria-label={expanded ? "Collapse row" : "Expand row"}
      title={expanded ? "Collapse row" : "Expand row"}
      data-testid={
        testidTable
          ? `${testidTable}-table-row-${rowIndex}-nested-toggle`
          : undefined
      }
      // Negative margins keep the enlarged hit area from moving the cell text.
      className="flex justify-center items-center float-left h-3 w-3 -my-0.5 -ml-0.5 mr-0.25 cursor-pointer"
    >
      {expanded ? (
        <ChevronUpIcon className="h-2 w-2" stroke="currentColor" />
      ) : (
        <ChevronDownIcon className="h-2 w-2" />
      )}
    </button>
  );
};

export default PillarTableNestedToggle;
